import React, { Suspense } from "react";
import {
  createHashRouter,
  Navigate,
  useParams,
} from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import AuthGuard from "../guards/AuthGuard";
import AdminLayout from "../layouts/AdminLayout";

// Lazy load pages
const LoginPage = React.lazy(() => import("../pages/LoginPage"));
const OverviewPage = React.lazy(() => import("../pages/OverviewPage"));
const TestBuildsPage = React.lazy(() => import("../pages/TestBuildsPage"));
const ProjectsPage = React.lazy(() => import("../pages/ProjectsPage"));
const ProjectDetailPage = React.lazy(() => import("../pages/ProjectDetailPage"));
const UploadsPage = React.lazy(() => import("../pages/UploadsPage"));
const AppPage = React.lazy(() => import("../pages/AppPage"));
const UsersPage = React.lazy(() => import("../pages/UsersPage"));
const PlaceholderPage = React.lazy(() => import("../pages/PlaceholderPage"));

const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;

/** Old /downloads/project/:id links (DownloadsPage removed) land on the project page. */
const LegacyProjectDownloadsRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/${id ?? ""}`} replace />;
};

export const router = createHashRouter([
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    path: "/login",
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },
  {
    element: (
      <AuthGuard>
        <AdminLayout />
      </AuthGuard>
    ),
    children: [
      { path: "/dashboard", element: <OverviewPage /> },
      { path: "/dashboard/:projectId", element: <ProjectDetailPage /> },
      { path: "/projects", element: <ProjectsPage /> },
      { path: "/test-builds", element: <TestBuildsPage /> },
      { path: "/users", element: <UsersPage /> },
      {
        path: "/access-rules",
        element: (
          <PlaceholderPage
            title="Access rules"
            description="Rules for who can see which builds will be managed here."
          />
        ),
      },
      {
        path: "/activity",
        element: (
          <PlaceholderPage
            title="Activity"
            description="An audit log of uploads, deletions and access changes will appear here."
          />
        ),
      },
    ],
  },
  {
    path: "/dashboard/uploads",
    element: (
      <SuspenseWrapper>
        <UploadsPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/downloads/project/:id",
    element: <LegacyProjectDownloadsRedirect />,
  },
  {
    path: "/downloads/:id",
    element: (
      <SuspenseWrapper>
        <AppPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

export default router;
